import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  CalendarIcon,
  CameraIcon
} from "lucide-react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../components/ui/avatar";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Header } from "../../components/Header/Header";
import { ComponentHeader } from "../../components/Header/ComponentHeader";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { format, parse } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { setFullName, setProfileImage } from "../../store/slices/userSlice";
import { toast } from "sonner";
import { userApi } from "../../lib/api";
import { useTranslation } from "react-i18next";
import ReactCrop, { Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { centerCrop, makeAspectCrop } from 'react-image-crop';

// Define validation schema
const profileSchema = z.object({
  name: z.string()
    .min(1, "nameRequired")
    .refine((val) => val.trim().length > 0, "nameRequired"),
  mobile: z.string(),
  email: z.string().email("invalidEmail").optional().or(z.literal("")),
  gender: z.string().optional(),
  dob: z.string().optional(),
  taluka: z.string().optional(),
  village: z.string().optional(),
  pincode: z.string().optional().refine(val => !val || /^\d{0,6}$/.test(val), "Pincode must be up to 6 digits"),
  cropAcreage: z.string().optional().or(z.literal("")).refine(val => {
    if (!val) return true;
    const num = parseFloat(val);
    return !isNaN(num) && num >= 0 && num <= 999.99;
  }, "Crop acreage must be between 0 and 999.99"),
});

type ProfileFormValues = z.infer<typeof profileSchema>;

// Add this after the imports
const GENDER_OPTIONS = [
  { id: 1, label: "male" },
  { id: 2, label: "female" },
  { id: 3, label: "other" }
] as const;

function getCenteredCrop(imageWidth: number, imageHeight: number, aspect = 1) {
  const isHorizontal = imageWidth > imageHeight;
  const cropSize = isHorizontal ? 80 : 90; // Use smaller percentage for horizontal images
  
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: cropSize,
        height: cropSize,
      },
      aspect,
      imageWidth,
      imageHeight
    ),
    imageWidth,
    imageHeight
  );
}

// Add ReadOnlyInput component
const ReadOnlyInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  (props, ref) => (
    <input
      {...props}
      ref={ref}
      readOnly
    />
  )
);

export const EditProfile = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const countryMatch = location.pathname.match(/^\/([a-z]{2,})\//i);
  const country = countryMatch ? countryMatch[1] : "in";
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [date, setDate] = React.useState<Date | undefined>(undefined);
  const [iseditable, setIsEditable] = React.useState<boolean>(false);
  const userId = useSelector((state: RootState) => state.user.userId);
  const fullName = useSelector((state: RootState) => state.user.fullName);
  const [localProfileImage, setLocalProfileImage] = useState<string>('');
  const [showCropModal, setShowCropModal] = useState(false);
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 90,
    height: 90,
    x: 5,
    y: 5
  });
  const [imageToCrop, setImageToCrop] = useState<string>('');
  const imageRef = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
  });

  const fetchUserData = async () => {
    if (!userId) {
      toast.error(t('userIdNotFound'));
      return;
    }

    try {
      const result = await userApi.getUserProfile(userId);

      if (result.status === 200 && result.data) {
        const { data } = result;
        setValue("name", data.fullName);
        setValue("mobile", data.mobileNumber);
        setValue("email", data.email || '');
        setValue("gender", data.gender?.toString() || '');
        setValue("cropAcreage", data.cropAcreage?.toString() || '');
        dispatch(setFullName(data.fullName));
        dispatch(setProfileImage(data.profileImage || ''));
        
        // Handle date of birth with better error handling
        if (data.dateOfBirth) {
          try {
            // Try parsing as 'dd/MM/yyyy' first
            let parsedDate = parse(data.dateOfBirth, 'dd/MM/yyyy', new Date());
            
            // If invalid, try 'dd MMM yyyy'
            if (isNaN(parsedDate.getTime())) {
              parsedDate = parse(data.dateOfBirth, 'dd MMM yyyy', new Date());
            }
            
            // If still invalid, try ISO format
            if (isNaN(parsedDate.getTime())) {
              parsedDate = new Date(data.dateOfBirth);
            }
            
            // Only set the date if it's valid
            if (!isNaN(parsedDate.getTime())) {
              const formattedDate = format(parsedDate, 'dd MMM yyyy');
              setValue("dob", formattedDate);
              setDate(parsedDate);
            } else {
              console.warn('Invalid date format received:', data.dateOfBirth);
              setValue("dob", '');
              setDate(undefined);
            }
          } catch (dateError) {
            console.warn('Error parsing date:', dateError);
            setValue("dob", '');
            setDate(undefined);
          }
        } else {
          setValue("dob", '');
          setDate(undefined);
        }
        
        setValue("taluka", data.taluka || '');
        setValue("village", data.village || '');
        setValue("pincode", data.pincode || '');
        setLocalProfileImage(data.profileImage || '');
      } else {
        toast.error(result.message || t('failedToFetchUserData'));
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      if (error instanceof Error) {
        toast.error(error.message || t('errorFetchingUserData'));
      } else {
        toast.error(t('errorFetchingUserData'));
      }
    }
  };

  useEffect(() => {
    fetchUserData();
  }, [userId, setValue]);

  const checkForChanges = () => {
    const currentValues = watch();
    const hasFormChanges = Object.keys(currentValues).some(key => {
      const value = currentValues[key as keyof ProfileFormValues];
      if (key === 'name') {
        return value !== undefined && value.trim().length > 0;
      }
      return value !== undefined && value !== '';
    });
    setHasChanges(hasFormChanges);
    return hasFormChanges;
  };

  const handleGoBack = () => {
    navigate(`/${country}/my-predictions`);
  };

  const handleDiscardChanges = () => {
    setShowDiscardDialog(false);
    setIsEditable(false);
    setHasChanges(false);
    // Reset form to initial values from fetchUserData
    fetchUserData().then(() => {
      // Clear all form errors by resetting the form
      reset(watch(), {
        keepErrors: false,
        keepDirty: false,
        keepIsSubmitted: false,
        keepTouched: false,
        keepIsValid: false,
        keepSubmitCount: false
      });
    });
  };

  const handleCancelDiscard = () => {
    setShowDiscardDialog(false);
  };

  const onSubmit = async (data: ProfileFormValues) => {
    if (!userId) {
      toast.error(t('userIdNotFound'));
      return;
    }

    try {
      // Create base object with all fields explicitly defined according to UpdateUserProfileRequest interface
      const updateData = {
        fullName: data.name ? data.name.trim() : "",
        mobileNumber: data.mobile || "",
        email: data.email ? data.email.trim() : "",
        gender: data.gender || "",
        taluka: data.taluka ? data.taluka.trim() : "",
        village: data.village ? data.village.trim() : "",
        pincode: data.pincode ? data.pincode.trim() : "",
        dateOfBirth: data.dob || "",
        profileImage: localProfileImage || "",
        cropAcreage: data.cropAcreage || ""
      };


      const result = await userApi.updateUserProfile(userId, updateData);
      
      if (result.status === 200) {
        toast.success(result.message);
        await fetchUserData();
        setIsEditable(false);
      } else {
        toast.error(result.message || t('failedToUpdateUser'));
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(t('errorUpdatingUser'));
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      toast.error(t('invalidImageFormat'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('imageSizeTooLarge'));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImageToCrop(reader.result as string);
      setShowCropModal(true);
    };
    reader.onerror = () => {
      toast.error(t('errorReadingFile'));
    };
    reader.readAsDataURL(file);

    // Clear the file input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCropComplete = (croppedArea: Crop) => {
    if (!imageRef.current) return;

    const canvas = document.createElement('canvas');
    const scaleX = imageRef.current.naturalWidth / imageRef.current.width;
    const scaleY = imageRef.current.naturalHeight / imageRef.current.height;
    canvas.width = croppedArea.width;
    canvas.height = croppedArea.height;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    ctx.drawImage(
      imageRef.current,
      croppedArea.x * scaleX,
      croppedArea.y * scaleY,
      croppedArea.width * scaleX,
      croppedArea.height * scaleY,
      0,
      0,
      croppedArea.width,
      croppedArea.height
    );

    // Compress to below 2MB
    let quality = 0.92; // Start with high quality
    let dataUrl = canvas.toDataURL('image/jpeg', quality);
    // The length of a base64 dataURL is about 1.37x the file size in bytes
    // So 2MB = 2 * 1024 * 1024 * 1.37 = ~2,865,152 characters
    const maxLength = 2 * 1024 * 1024 * 1.37;
    while (dataUrl.length > maxLength && quality > 0.1) {
      quality -= 0.05;
      dataUrl = canvas.toDataURL('image/jpeg', quality);
    }
    if (dataUrl.length > maxLength) {
      toast.error(t('croppedImageTooLarge'));
      return;
    }

    setLocalProfileImage(dataUrl);
    setShowCropModal(false);
  };

  const handleCameraClick = () => {
    if (!iseditable) {
      toast.error(t('enableEditModeFirst'));
      return;
    }
    fileInputRef.current?.click();
  };

  return (
    <div>
      <Header className="hidden sm:flex" />
      <ComponentHeader name={t('myProfile')} handleClick={handleGoBack} />
      <div className="bg-[#fcfcfc] flex flex-row justify-center w-full">
        <div className="bg-[#fcfcfc] w-full relative">
          {/* Sidebar */}
          <nav className="fixed left-0 top-[60px]  hidden sm:flex  flex-col w-[100px] h-[calc(100vh-60px)] items-start gap-5 p-2.5 bg-primary-new500">
            <div 
              className="cursor-pointer relative w-20 h-20 bg-[#014224] rounded-[10px] flex items-center justify-center" 
              onClick={handleGoBack}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleGoBack();
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={t('back')}
            >
              <img
                className="w-11 h-11"
                alt={t('iconPrediction')}
                src="https://c.animaapp.com/m9ttivnnw25t6a/img/icon-prediction.svg"
              />
            </div>
          </nav>

          {/* Main Content */}
          <main className="sm:ml-[100px] pt-5  sm:w-[calc(100%-100px)] h-[calc(100dvh-60px)] overflow-auto sm:px-[24px]">
            <form onSubmit={handleSubmit(onSubmit)}>
              <Card className="w-full sm:shadow-[0px_0px_10px_2px_#0000001a]">
                <CardContent className="p-0 sm:px-6 pb-2 sm:py-4 px-[24px]">
                  <div className="flex flex-col gap-[18px]">
                    {/* Profile Picture */}
                    <div className="relative w-[93px] h-[83px]">
                      <Avatar className="w-20 h-20">
                        {localProfileImage ? (
                          <AvatarImage asChild>
                            <img
                              src={localProfileImage}
                              alt={t('profilePic', { defaultValue: 'Profile Picture' })}
                              className="w-full h-full object-cover"
                            />
                          </AvatarImage>
                        ) : (
                          <AvatarFallback>
                            {(() => {
                              const names = fullName?.split(' ') || [];
                              return names.length > 1 
                                ? (names[0][0] + names[1][0]).toUpperCase()
                                : names[0]?.[0]?.toUpperCase() || '';
                            })()}
                          </AvatarFallback>
                        )}
                      </Avatar>
                      <div 
                          className={`absolute w-9 h-9 top-[47px] left-[57px] rounded-[28px] flex items-center justify-center transition-colors ${iseditable ? 'bg-primary-new500 cursor-pointer opacity-100 hover:bg-primary-new700' : 'bg-gray-200 cursor-not-allowed opacity-50'}`}
                          title={iseditable ? t('uploadProfilePicture') : t('enableEditModeFirst')}
                          onClick={handleCameraClick}
                          style={{ pointerEvents: iseditable ? 'auto' : 'none' }}
                        >
                          <CameraIcon className="w-6 h-6 text-white" />
                        </div>
                    
                    </div>

                    <div className="flex flex-col gap-6">
                      {/* Personal Details Section */}
                      <div className="flex flex-col gap-[17px]">
                        <h2 className="font-medium text-black text-sm">
                          {t('personalDetails')}
                        </h2>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div className="flex flex-col gap-2">
                            <label className="text-sm font-normal text-black">
                              {t('fullName')}<span className="text-red-500">*</span>
                            </label>
                            <Input
                              className="h-[50px] border border-[#c2c7d0] shadow-[0px_0px_4px_#0000001a]"
                              {...register("name")}
                              disabled={!iseditable}
                              placeholder={t('enterFullName')}
                              data-testid="name-input"
                              maxLength={50}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const value = e.target.value;
                                if (value.length <= 50) {
                                  setValue("name", value, { shouldValidate: true });
                                  checkForChanges();
                                }
                              }}
                            />
                            {errors.name && (
                              <span className="text-red-500 text-xs">{t(errors.name?.message || '')}</span>
                            )}
                          </div>

                          <div className="flex flex-col gap-2">
                            <label className="text-sm font-normal text-black">
                              {t('mobileNumber')}<span className="text-red-500">*</span>
                            </label>
                            <Input
                              className="h-[50px] bg-gray-100"
                              {...register("mobile")}
                              disabled
                              data-testid="mobile-input"
                            />
                          </div>

                          <div className="flex flex-col gap-2">
                            <label className="text-sm font-normal text-black">
                              {t('emailId')}
                            </label>
                            <Input
                              className="h-[50px] border border-[#c2c7d0] shadow-[0px_0px_4px_#0000001a]"
                              {...register("email")}
                              disabled={!iseditable}
                              placeholder={t('enterEmail')}
                              data-testid="email-input"
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setValue("email", e.target.value, { shouldValidate: true });
                                checkForChanges();
                              }}
                            />
                            {errors.email && (
                              <span className="text-red-500 text-xs">{t(errors.email?.message || '')}</span>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div className="flex flex-col gap-2 w-full">
                            <label className="text-sm font-normal text-black">
                              {t('dateOfBirth')}
                            </label>
                            <div className="relative w-full">
                              <DatePicker
                                selected={date}
                                onChange={(selectedDate: Date | null) => {
                                  if (selectedDate) {
                                    setDate(selectedDate);
                                    setValue("dob", format(selectedDate, "dd MMM yyyy"));
                                    checkForChanges();
                                  } else {
                                    setDate(undefined);
                                    setValue("dob", "");
                                    checkForChanges();
                                  }
                                }}
                                dateFormat="dd MMM yyyy"
                                placeholderText={t('selectDate')}
                                className="h-[50px] min-w-full border border-[#c2c7d0] shadow-[0px_0px_4px_#0000001a] rounded-md px-3"
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                isClearable
                                maxDate={new Date()}
                                disabled={!iseditable}
                                customInput={
                                  <ReadOnlyInput
                                    className={`h-[50px] min-w-full border border-[#c2c7d0] shadow-[0px_0px_4px_#0000001a] rounded-md px-3 ${!iseditable ? 'bg-gray-100 cursor-not-allowed text-gray-400' : 'cursor-pointer'}`}
                                    style={{ caretColor: 'transparent' }}
                                    disabled={!iseditable}
                                  />
                                }
                              />
                              <CalendarIcon className="absolute right-6 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                            </div>
                            {errors.dob && (
                              <span className="text-red-500 text-xs">{t(errors.dob?.message || '')}</span>
                            )}
                          </div>

                          <div className="flex flex-col gap-2">
                            <label className="text-sm font-normal text-black">
                              {t('gender')}
                            </label>
                            <Select
                              value={watch('gender')}
                              onValueChange={(value) => {
                                setValue('gender', value);
                                checkForChanges();
                              }}
                              disabled={!iseditable}
                            >
                              <SelectTrigger className="h-[50px] border border-[#c2c7d0] shadow-[0px_0px_4px_#0000001a]" data-testid="gender-select">
                                <SelectValue placeholder={t('selectGender')} />
                              </SelectTrigger>
                              <SelectContent>
                                {GENDER_OPTIONS.map((gender) => (
                                  <SelectItem key={gender.id} value={gender.id.toString()}>
                                    {t(gender.label)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>

                      {/* Address Section */}
                      <div className="flex flex-col gap-2">
                        <h2 className="font-medium text-black text-sm">
                          {t('myAddress')}
                        </h2>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div className="flex flex-col gap-2">
                            <label className="text-sm font-normal text-black">
                              {t('taluka')}
                            </label>
                            <Input
                              className="h-[50px] border border-[#c2c7d0] shadow-[0px_0px_4px_#0000001a]"
                              {...register("taluka")}
                              placeholder={t('enterTaluka')}
                              disabled={!iseditable}
                              data-testid="taluka-input"
                              maxLength={50}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const value = e.target.value;
                                if (value.length <= 50) {
                                  setValue("taluka", value, { shouldValidate: true });
                                  checkForChanges();
                                }
                              }}
                            />
                            {errors.taluka && (
                              <span className="text-red-500 text-xs">{t(errors.taluka?.message || '')}</span>
                            )}
                          </div>

                          <div className="flex flex-col gap-2">
                            <label className="text-sm font-normal text-black">
                              {t('village')}
                            </label>
                            <Input
                              className="h-[50px] border border-[#c2c7d0] shadow-[0px_0px_4px_#0000001a]"
                              {...register("village")}
                              placeholder={t('enterVillage')}
                              disabled={!iseditable}
                              data-testid="village-input"
                              maxLength={50}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const value = e.target.value;
                                if (value.length <= 50) {
                                  setValue("village", value, { shouldValidate: true });
                                  checkForChanges();
                                }
                              }}
                            />
                            {errors.village && (
                              <span className="text-red-500 text-xs">{t(errors.village?.message || '')}</span>
                            )}
                          </div>

                          <div className="flex flex-col gap-2">
                            <label className="text-sm font-normal text-black">
                              {t('pincode')}
                            </label>
                            <Input
                              className="h-[50px] border border-[#c2c7d0] shadow-[0px_0px_4px_#0000001a]"
                              {...register("pincode")}
                              placeholder={t('enterPincode')}
                              disabled={!iseditable}
                              data-testid="pincode-input"
                              maxLength={6}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const onlyNums = e.target.value.replace(/[^0-9]/g, '');
                                setValue("pincode", onlyNums, { shouldValidate: true });
                                checkForChanges();
                              }}
                            />
                            {errors.pincode && (
                              <span className="text-red-500 text-xs">{t(errors.pincode?.message || '')}</span>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div className="flex flex-col gap-2">
                            <label className="text-sm font-normal text-black">
                              {t('cropAcreage')}
                            </label>
                            <Input
                              className="h-[50px] border border-[#c2c7d0] shadow-[0px_0px_4px_#0000001a]"
                              {...register("cropAcreage")}
                              placeholder={t('enterCropAcreage')}
                              disabled={!iseditable}
                              data-testid="crop-acreage-input"
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const value = e.target.value;
                                // Allow empty value
                                if (!value) {
                                  setValue("cropAcreage", "", { shouldValidate: true });
                                  checkForChanges();
                                  return;
                                }
                                // Allow only numbers and one decimal point with up to 2 decimal places
                                if (/^\d*\.?\d{0,2}$/.test(value)) {
                                  const num = parseFloat(value);
                                  if (!isNaN(num) && num >= 0 && num <= 999.99) {
                                    setValue("cropAcreage", value, { shouldValidate: true });
                                    checkForChanges();
                                  }
                                }
                              }}
                            />
                            {errors.cropAcreage && (
                              <span className="text-red-500 text-xs">{t(errors.cropAcreage?.message || '')}</span>
                            )}
                          </div>
                        </div>


                      </div>
                    </div>
                  </div>
                </CardContent>

                {/* Footer with action buttons */}
                <div className="flex justify-end gap-2.5 px-6 py-4 shadow-mobile-footer-shadow bg-white sm:bg-none sm:shadow-none px-[24px]">
                  {iseditable && (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-[154px] border-primary-new500 text-primary-new500 rounded-[100px]"
                      onClick={() => {
                        if (hasChanges) {
                          setShowDiscardDialog(true);
                        } else {
                          setIsEditable(false);
                        }
                      }}
                      disabled={!hasChanges}
                    >
                      {t('discardChanges')}
                    </Button>
                  )}
                  <Button 
                    type="button"
                    className="w-[154px] bg-primary-new500 text-white rounded-[100px]"
                    onClick={() => {
                      if (iseditable) {
                        handleSubmit(onSubmit)();
                      } else {
                        setIsEditable(true);
                      }
                    }}
                  >
                    {iseditable ? t('update') : t('edit')}
                  </Button>
                </div>
              </Card>
            </form>
          </main>
        </div>
      </div>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImageUpload}
        accept=".jpg,.jpeg,.png"
        style={{ display: 'none' }}
        data-testid="file-input"
      />
      
      {/* Crop Modal */}
      <Dialog open={showCropModal} onOpenChange={setShowCropModal}>
        <DialogContent className="max-w-[90vw] sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{t('cropImage')}</DialogTitle>
          </DialogHeader>
          <div className="relative w-full h-[400px] overflow-hidden bg-gray-100">
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              aspect={1}
              className="w-full h-full"
              minWidth={100}
              minHeight={100}
            >
              <img
                ref={imageRef}
                src={imageToCrop}
                alt={t('cropPreview')}
                className="w-full h-full object-cover"
                onLoad={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  const img = e.currentTarget as HTMLImageElement;
                  setCrop(getCenteredCrop(img.naturalWidth, img.naturalHeight, 1));
                }}
              />
            </ReactCrop>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCropModal(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={() => handleCropComplete(crop)}>
              {t('crop')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Discard Changes Dialog */}
      <Dialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <DialogContent>
          <>
            <DialogHeader>
              <DialogTitle>{t('discardChangesTitle', { defaultValue: 'Discard Changes?' })}</DialogTitle>
              <DialogDescription>{t('discardChangesMessage', { defaultValue: 'Are you sure you want to discard your changes?' })}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={handleCancelDiscard}>
                {t('no', { defaultValue: 'No' })}
              </Button>
              <Button variant="destructive" onClick={handleDiscardChanges}>
                {t('yesDiscard', { defaultValue: 'Yes' })}
              </Button>
            </DialogFooter>
          </>
        </DialogContent>
      </Dialog>
    </div>
  );
};

